<template>
    <div v-if="customerId">
        <b-card>
            <template #header>
                <div class="row">
                    <div class="col-8">
                        <h4 class="m-0, pt-1">Users / Contacts</h4>
                    </div>
                    <div class="col-4">

                        <b-button class="float-right mx-2" variant="success" @click="save()" v-b-popover.hover.top="'Save'">
                            <font-awesome-icon :icon="['fas', 'save']"/>
                        </b-button>
                        <b-button class="float-right mx-2" variant="primary" @click="addUser" v-b-popover.hover.top="'Add New'">
                            <font-awesome-icon :icon="['fas', 'plus']"/>
                        </b-button>
                        <b-button class="float-right mx-2" variant="secondary" @click="openBulkModal" v-b-popover.hover.top="'Bulk Upload'" v-if="isSWAdmin">
                            <font-awesome-icon :icon="['fas', 'upload']"/>
                        </b-button>
                        <b-button id="roles_info_btn" class="float-right px-3 mx-2" variant="info" aria-label="Roles Info">
                            <font-awesome-icon :icon="['fas', 'info']"/>
                        </b-button>

                        <!-- Roles info button -->
                        <b-popover target="roles_info_btn" triggers="hover focus" placement="left">
                            <div><strong>Role descriptions</strong></div>
                            <small>
                            <ul class="mb-0 pl-3">
                                <li>
                                    <strong>Admin:</strong> Manages users and all orders.
                                </li>
                                <li>
                                    <strong>Viewer:</strong> Views all organization orders and can place orders.
                                </li>
                                <li>
                                    <strong>Shopper:</strong> Places orders and views only their own.
                                </li>
                            </ul>
                            </small>
                            <b-table-simple striped small responsive>
                                <b-thead>
                                    <b-tr>
                                        <b-th><small></small></b-th>
                                        <b-th><small>Admin</small></b-th>
                                        <b-th><small>Viewer</small></b-th>
                                        <b-th><small>Shopper</small></b-th>
                                    </b-tr>
                                </b-thead>
                                <b-tbody>
                                    <b-tr>
                                        <b-td><small>Manage users</small></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'check']"   class="text-success"/></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'xmark']"   class="text-success"/></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'xmark']"   class="text-success"/></b-td>
                                    </b-tr>
                                    <b-tr>
                                        <b-td><small>View all orders</small></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'check']"   class="text-success"/></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'check']"   class="text-success"/></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'xmark']"   class="text-danger"/></b-td>
                                    </b-tr>
                                    <b-tr>
                                        <b-td><small>View own orders</small></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'check']"   class="text-success"/></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'check']"   class="text-success"/></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'check']"   class="text-success"/></b-td>
                                    </b-tr>
                                    <b-tr>
                                        <b-td><small>Place orders</small></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'check']"   class="text-success"/></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'check']"   class="text-success"/></b-td>
                                        <b-td><font-awesome-icon :icon="['fas', 'check']"   class="text-success"/></b-td>
                                    </b-tr>
                                </b-tbody>
                            </b-table-simple>
                            <small><strong>Note</strong>: Only a Studies Weekly administrator can assign the Admin role.</small>
                        </b-popover>
                    </div>
                </div>
            </template>

            <!-- Search & Filters -->
            <div class="d-flex align-items-center mb-3 flex-wrap">
                <b-input-group size="sm" class="mr-3" style="max-width: 260px;">
                    <b-input-group-prepend>
                        <b-input-group-text><font-awesome-icon :icon="['fas', 'search']"/></b-input-group-text>
                    </b-input-group-prepend>
                    <b-input v-model="userSearch" placeholder="Search by email..." @input="currentPage = 1"></b-input>
                    <b-input-group-append v-if="userSearch">
                        <b-button variant="outline-secondary" @click="userSearch = ''; currentPage = 1">
                            <font-awesome-icon :icon="['fas', 'xmark']"/>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>

                <b-button-group size="sm" class="mr-2">
                    <b-button
                        v-for="opt in roleFilterOptions"
                        :key="opt.value"
                        :variant="roleFilter === opt.value ? 'primary' : 'outline-secondary'"
                        @click="roleFilter = opt.value; currentPage = 1"
                    >{{ opt.label }}</b-button>
                </b-button-group>

                <b-button-group size="sm">
                    <b-button :variant="accountFilter === null ? 'dark' : 'outline-secondary'" @click="accountFilter = null; currentPage = 1">All</b-button>
                    <b-button :variant="accountFilter === true ? 'success' : 'outline-secondary'" @click="accountFilter = true; currentPage = 1" v-b-popover.hover.top="'Has store account'">Has Account</b-button>
                    <b-button :variant="accountFilter === false ? 'warning' : 'outline-secondary'" @click="accountFilter = false; currentPage = 1" v-b-popover.hover.top="'No store account'">No Account</b-button>
                </b-button-group>

                <small v-if="filteredUsers.length !== customerUsers.length" class="text-muted ml-3">
                    Showing {{ filteredUsers.length }} of {{ customerUsers.length }}
                </small>
            </div>

            <b-table v-if="customerUsers" :items="filteredUsers" :fields="userFields" :busy="isLoading || isSaving" striped
                     :per-page="perPage" :current-page="currentPage">

                <template #cell(companyId)></template>
                <template #cell(username)="data">
                    <b-input v-model="data.item.username"
                             type="text"
                             name="contact"
                             placeholder="Email"
                             class="w-100"
                             :class="{'border-danger': !isValidEmail(data.item.username) && data.item.username.length > 0}"
                             :disabled="data.item.id !== null"
                    ></b-input>
                </template>
                <template #cell(isUser)="data">
                    <b-checkbox :id="`isUser_${data.index}`"
                                :disabled="true"
                                :checked="data.item.user_id !== null"
                                v-b-popover.hover.top="data.item.user_id === null ? `Doesn't have a store account` : `Has store account`"
                    />
                </template>
                <template #cell(permission)="data">
                    <multiselect v-model="data.item.permission"
                                 :options="permissions"
                                 name="permission"
                                 placeholder="Permissions"
                                 :searchable="true"
                                 select-label=""
                                 deselect-label=""
                                 :disabled="data.item.permission_locked"
                    >
                    </multiselect>
                </template>
                <template #cell(actions)="data">
                    <b-btn variant="danger" class="mr-2"  @click="openDeleteModal(data.item)" :disabled="data.item.permission_locked">
                        <font-awesome-icon :icon="['fas', 'trash']" />
                    </b-btn>
                    <b-btn :id="`info_${data.index}`" variant="info">
                        <font-awesome-icon :icon="['fas', 'clipboard-list']" />
                    </b-btn>

                    <b-popover :target="`info_${data.index}`" triggers="hover" placement="top">
                        <div><strong>Added by</strong>:
                            <span v-if="hasStudiesWeeklyCreator(data.item)">Studies Weekly Admin</span>
                            <span v-else>{{data.item.creator}}</span>
                        </div>
                        <div><strong>Added on</strong>: {{data.item.created_at}}</div>
                    </b-popover>
                </template>
            </b-table>

            <b-pagination
                v-if="filteredUsers && filteredUsers.length > perPage"
                v-model="currentPage"
                :total-rows="filteredUsers.length"
                :per-page="perPage"
                align="center"
                class="mt-2 mb-0"
            ></b-pagination>

        </b-card>

        <!-- Email -->
        <!-- Permissions -->

        <b-modal id="deleteUserModal"  v-if="userToDelete"  @ok="deleteUser()"ok-variant="danger" ok-title="Delete">
            <template #modal-header>
                Delete User
            </template>
            Are you sure you want to delete this user? ({{userToDelete.username}})
        </b-modal>

        <!-- Bulk Upload Modal -->
        <b-modal id="bulkUploadModal" size="lg" title="Bulk Upload Users" @hide="resetBulkModal" hide-footer>
            <div v-if="bulkStep === 1">
                <p class="text-muted">Paste any text containing email addresses below. All emails found will be extracted.</p>
                <b-form-textarea
                    v-model="bulkText"
                    rows="8"
                    placeholder="Paste emails or any text containing emails here..."
                ></b-form-textarea>
                <div class="mt-2 text-right">
                    <b-button variant="secondary" @click="$bvModal.hide('bulkUploadModal')" class="mr-2">Cancel</b-button>
                    <b-button variant="primary" @click="extractEmails" :disabled="!bulkText.trim()">Extract Emails</b-button>
                </div>
            </div>
            <div v-else-if="bulkStep === 2">
                <p class="text-muted">
                    Found <strong>{{ bulkEmails.length }}</strong> email(s). Set the role for each, then click <strong>Add to List</strong>.
                </p>
                <div class="mb-2 d-flex align-items-center">
                    <small class="mr-2 text-muted">Set all to:</small>
                    <b-select :options="bulkPermissionOptions" v-model="bulkDefaultPermission" size="sm" style="width:120px" class="mr-2"></b-select>
                    <b-button size="sm" variant="outline-secondary" @click="applyDefaultToAll">Apply to All</b-button>
                </div>
                <b-table :items="bulkEmails" :fields="bulkFields" small striped>
                    <template #cell(email)="data">
                        <span :class="{'text-danger': isDuplicateEmail(data.item.email)}">
                            {{ data.item.email }}
                            <small v-if="isDuplicateEmail(data.item.email)" class="text-danger ml-1">(already in list)</small>
                        </span>
                    </template>
                    <template #cell(permission)="data">
                        <b-select :options="bulkPermissionOptions" v-model="data.item.permission" size="sm"></b-select>
                    </template>
                    <template #cell(remove)="data">
                        <b-button size="sm" variant="outline-danger" @click="bulkEmails.splice(data.index, 1)">
                            <font-awesome-icon :icon="['fas', 'trash']"/>
                        </b-button>
                    </template>
                </b-table>
                <div class="mt-2 d-flex justify-content-between align-items-center">
                    <b-button variant="outline-secondary" @click="bulkStep = 1">Back</b-button>
                    <div>
                        <b-button variant="secondary" @click="$bvModal.hide('bulkUploadModal')" class="mr-2">Cancel</b-button>
                        <b-button variant="success" @click="finalizeBulk" :disabled="bulkEmails.length === 0">
                            Add {{ bulkEmails.length }} User(s) to List
                        </b-button>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
export default {
    name: "CustomerUsers",
    props: ['customerId'],
    data: () => ({
        customerUsers: [],
        userToDelete: null,
        isLoading: false,
        isSaving: false,
        userFields: ['username', {key:'isUser', label:''}, 'permission', 'actions'],
        permissions: [
            'view', 'shop'
        ],
        // Pagination
        perPage: 15,
        currentPage: 1,
        // Search & filters
        userSearch: '',
        roleFilter: 'all',
        accountFilter: null,
        // Bulk upload
        bulkText: '',
        bulkEmails: [],
        bulkStep: 1,
        bulkDefaultPermission: 'shop',
        bulkFields: [
            { key: 'email', label: 'Email' },
            { key: 'permission', label: 'Role' },
            { key: 'remove', label: '' },
        ],
    }),
    computed: {
        isSWAdmin() {
            return this.$auth.check('admin');
        },
        bulkPermissionOptions() {
            return this.permissions;
        },
        roleFilterOptions() {
            const opts = [{ value: 'all', label: 'All Roles' }];
            for (const p of this.permissions) {
                opts.push({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) });
            }
            return opts;
        },
        filteredUsers() {
            return this.customerUsers.filter(u => {
                const searchMatch = !this.userSearch ||
                    (u.username && u.username.toLowerCase().includes(this.userSearch.toLowerCase()));
                const roleMatch = this.roleFilter === 'all' || u.permission === this.roleFilter;
                const accountMatch = this.accountFilter === null ||
                    (this.accountFilter === true ? u.user_id !== null : u.user_id === null);
                return searchMatch && roleMatch && accountMatch;
            });
        },
    },
    watch: {
        customerId() {
            this.fetchUsers();
        }
    },
    methods: {
        hasStudiesWeeklyCreator(userRole) {
            return userRole.creator != null && userRole.creator.includes('@studiesweekly.com');
        },
        addUser() {
            // push to TOP of customerUsers array
            this.customerUsers.unshift({id: null, username: "", permission: "shop", user_id: null, permission_locked: false});
        },
        fetchUsers() {
            if (!this.customerId)
                return;

            this.isLoading = true;
            let canEditAdmin = this.isSWAdmin;
            this.axios.get(`/dashboard/customer/${this.customerId}/users`)
                .then(response => {
                    this.customerUsers = [];
                    for (let user of response.data) {
                        let creator = user.creator ? user.creator.username : 'Contact Form';
                        this.customerUsers.push({
                            id: user.id,
                            customer_id: user.customer_id,
                            user_id: user.user_id,
                            title: user.title,
                            username: user.username,
                            permission: user.permission || 'shop',
                            permission_locked: (user.permission === 'admin' && !canEditAdmin),
                            creator: creator,
                            created_at: user.created_at,
                            updated_at: user.updated_at,
                        })
                    }
                    // sort by permission: admin > view > shop --> then by username
                    const permissionOrder = {admin: 1, view: 2, shop: 3};
                    this.customerUsers.sort((a, b) => {
                        if (permissionOrder[a.permission] !== permissionOrder[b.permission]) {
                            return permissionOrder[a.permission] - permissionOrder[b.permission];
                        }
                        return a.username.localeCompare(b.username);
                    });
                })
                .catch(error => {
                    console.error('Error fetching customer users:', error);
                }).finally(() => {
                this.isLoading = false;
            })
        },
        openDeleteModal(userToDelete) {
            this.userToDelete = userToDelete;
            this.$nextTick(() => {
                this.$bvModal.show('deleteUserModal');
            })
        },
        deleteUser() {
            this.customerUsers.splice(this.customerUsers.indexOf(this.userToDelete), 1);
            this.save();
        },
        save() {
            this.isSaving = true;

            this.axios.post(`/dashboard/customer/${this.customerId}/users`, {customer_id: this.customerId, users:this.customerUsers})
                .then(response => {
                    this.$bvToast.toast("saved successfully", {
                        variant: 'info',
                        solid: true,
                        noCloseButton: true,
                        autoHideDelay: 2000
                    });
                    this.fetchUsers();
                })
                .catch(error => {
                    this.$swal('Error saving users: ' + error);
                }).finally(() => {
                this.isSaving = false;
            })
        },
        sendResetPassword(customerUser) {
            return false;
            this.isSaving = true;

            // Validate email before sending reset password
            if (!this.isValidEmail(customerUser.username)) {
                this.$swal({
                    toast: true,
                    html: 'Invalid email address.',
                    type: 'error',
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 5000
                });
                this.isSaving = false;
                return;
            }
        },
        isValidEmail(email) {
            if (!email)
                return false;

            // https://emailregex.com/ -- RFC 5322 Standard
            const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            let match = email.match(emailRegex);
            return match != null;
        },
        openBulkModal() {
            this.resetBulkModal();
            this.$bvModal.show('bulkUploadModal');
        },
        resetBulkModal() {
            this.bulkText = '';
            this.bulkEmails = [];
            this.bulkStep = 1;
            this.bulkDefaultPermission = 'shop';
        },
        extractEmails() {
            const emailRegex = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g;
            const found = this.bulkText.match(emailRegex) || [];
            // Deduplicate within the found list
            const unique = [...new Set(found.map(e => e.toLowerCase()))];
            this.bulkEmails = unique.map(email => ({ email, permission: this.bulkDefaultPermission }));
            this.bulkStep = 2;
        },
        applyDefaultToAll() {
            this.bulkEmails = this.bulkEmails.map(e => ({ ...e, permission: this.bulkDefaultPermission }));
        },
        isDuplicateEmail(email) {
            return this.customerUsers.some(u => u.username && u.username.toLowerCase() === email.toLowerCase());
        },
        finalizeBulk() {
            for (const entry of this.bulkEmails) {
                this.customerUsers.push({
                    id: null,
                    username: entry.email,
                    permission: entry.permission,
                    user_id: null,
                    permission_locked: false,
                });
            }
            this.$bvModal.hide('bulkUploadModal');
        },
    },
    created() {
        this.fetchUsers();
    },
    mounted() {
        if (this.isSWAdmin)
            this.permissions.push('admin');
    }
}
</script>

<style scoped>

</style>
